import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useCallback, useState } from "react";
import {
  useDismissWorklistClusterMutation,
  useUndismissWorklistClusterMutation,
} from "store/queries/benefitComparison";
import {
  IWorklistClusterKey,
  IWorklistRow,
} from "types/BenefitComparison/BenefitComparison";

import { COMPARISON_BENEFIT_LABELS } from "./benefitComparisonWording";
import { ICelebrationOrigin, WorklistCelebration } from "./WorklistCelebration";

/**
 * "HIDE THIS CLUSTER" — TAKE A WHOLE WORKLIST CLUSTER OFF THE LIST.
 *
 * Neutral on purpose: a reviewer hides a cluster when the engine is right, but also when the cause
 * is elsewhere (the order's payer was never switched, a data or configuration problem upstream).
 * The stored reason is still `ENGINE_CORRECT` — the only enum value — and is not shown.
 *
 * 🚨 IT WRITES NO VERDICT. Marking every check in the cluster with a verdict was rejected: verdicts are
 * scored truth, so it would count values nobody reviewed as verified and inflate the engine's
 * accuracy. This only takes the row off the list — and puts it back, tagged, the moment today's
 * engine reads a value outside the range it had when it was marked.
 */

export const DISMISSED_TAG = "Hidden";
export const STALE_TAG = "Engine changed since hidden";

export const clusterKeyOf = (row: IWorklistRow): IWorklistClusterKey | null =>
  row.benefit === null
    ? null
    : {
        accountId: row.accountId,
        payerId: row.payerId,
        planName: row.planName,
        benefit: row.benefit,
        readFrom: row.readFrom,
      };

/** "Oct 7, 2026" — the date on a hidden or flagged cluster. Shared with `BenefitWorklistFlag.tsx`. */
export const dateOf = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/** PrimeReact's icon-only button, as the worklist's action cell draws its secondary actions. */
export const ICON_BUTTON_CLASS = "p-button-text p-button-rounded p-button-sm";

/**
 * The hidden-cluster STATUS: the tag (with the note as its tooltip) and the date it was hidden.
 * Nothing when the cluster is not hidden. Drawn beneath the action buttons.
 */
export function WorklistDismissalStatus({
  row,
  tooltipClass,
}: {
  row: IWorklistRow;
  tooltipClass: string;
}) {
  const { dismissal } = row;
  if (clusterKeyOf(row) === null || !dismissal) return null;
  return (
    <>
      <Tag
        value={dismissal.stale ? STALE_TAG : DISMISSED_TAG}
        severity={dismissal.stale ? "warning" : "success"}
        className={`white-space-nowrap ${tooltipClass}`}
        data-pr-tooltip={`${dateOf(dismissal.dismissedAt)}${
          dismissal.note ? ` — ${dismissal.note}` : ""
        }`}
        data-testid={
          dismissal.stale
            ? "benefitWorklist.dismissal.stale"
            : "benefitWorklist.dismissal"
        }
      />
      <span className="text-xs text-color-secondary white-space-nowrap">
        {dateOf(dismissal.dismissedAt)}
      </span>
    </>
  );
}

/**
 * The hide BUTTONS: hide when the cluster is not hidden — or when the mark has gone stale — and Undo
 * when it is hidden.
 *
 * Icons with tooltips since "Flag for review" joined them: three text links did not fit the action
 * cell. 🚨 The accessible names are still "Hide cluster" and "Undo hide" (`aria-label`), so a screen
 * reader — and `getByRole("button", { name: "Hide cluster" })` — reads what the old text said.
 * `data-pr-tooltip` needs the tab's one mounted `<Tooltip>`, matched by `tooltipClass`.
 */
export function WorklistDismissalButtons({
  row,
  tooltipClass,
  onDismiss,
}: {
  row: IWorklistRow;
  tooltipClass: string;
  onDismiss: (row: IWorklistRow) => void;
}) {
  const [undismiss, { isLoading }] = useUndismissWorklistClusterMutation();
  const key = clusterKeyOf(row);
  // A configuration gap covers every benefit and is not a cluster the engine answered.
  if (key === null) return null;
  const { dismissal } = row;
  return (
    <>
      {(!dismissal || dismissal.stale) && (
        <Button
          type="button"
          icon="pi pi-eye-slash"
          aria-label="Hide cluster"
          className={`${ICON_BUTTON_CLASS} ${tooltipClass}`}
          data-pr-tooltip="Hide cluster"
          data-testid="benefitWorklist.dismiss"
          onClick={() => onDismiss(row)}
        />
      )}
      {dismissal && (
        <Button
          type="button"
          icon="pi pi-undo"
          aria-label="Undo hide"
          className={`${ICON_BUTTON_CLASS} ${tooltipClass}`}
          data-pr-tooltip="Undo hide"
          loading={isLoading}
          data-testid="benefitWorklist.undismiss"
          onClick={() => undismiss(key)}
        />
      )}
    </>
  );
}

/** The small confirm behind "Hide cluster", with an optional note saying why. */
export function DismissClusterDialog({
  row,
  onHide,
  onDismissed,
}: {
  row: IWorklistRow | null;
  onHide: () => void;
  /**
   * Called once the cluster is hidden, after the dialog closes. The cluster detail view passes it to
   * go back to the list; the list itself passes nothing, because the row simply leaves.
   */
  onDismissed?: () => void;
}) {
  const [note, setNote] = useState("");
  // CLE-2895 — where the confirm button was when a dismiss SUCCEEDED; null = no celebration showing.
  const [celebration, setCelebration] = useState<ICelebrationOrigin | null>(
    null
  );
  const endCelebration = useCallback(() => setCelebration(null), []);
  const [dismiss, { isLoading, isError }] = useDismissWorklistClusterMutation();
  const key = row ? clusterKeyOf(row) : null;
  const close = () => {
    setNote("");
    onHide();
  };
  return (
    <>
      {celebration && (
        <WorklistCelebration origin={celebration} onDone={endCelebration} />
      )}
      <Dialog
        header="Hide this cluster?"
        visible={key !== null}
        style={{ width: "min(32rem, 95vw)" }}
        onHide={close}
        footer={
          <>
            <Button
              type="button"
              label="Cancel"
              className="p-button-text"
              onClick={close}
            />
            <Button
              type="button"
              label="Hide this cluster"
              loading={isLoading}
              data-testid="benefitWorklist.dismiss.confirm"
              onClick={async (event) => {
                if (!key) return;
                // Read before the await: the button is gone once the dialog closes.
                const box = event.currentTarget.getBoundingClientRect();
                const result = await dismiss({
                  ...key,
                  ...(note.trim() ? { note: note.trim() } : {}),
                });
                if (!("error" in result)) {
                  setCelebration({
                    x: box.left + box.width / 2,
                    y: box.top + box.height / 2,
                  });
                  close();
                  onDismissed?.();
                }
              }}
            />
          </>
        }
        data-testid="benefitWorklist.dismiss.dialog"
      >
        {row && key && (
          <div className="flex flex-column gap-3">
            <span>
              {row.accountName} · {row.payerName} ·{" "}
              {row.planName ?? "No plan name"} ·{" "}
              {COMPARISON_BENEFIT_LABELS[key.benefit]}
            </span>
            <span className="text-color-secondary">
              Hiding takes this cluster off the worklist, whether the engine is
              right here or the cause is elsewhere (for example the order&apos;s
              payer should have been switched, or a data or configuration
              problem upstream). No verdict is recorded and no accuracy figure
              changes. It comes back, tagged, if the engine later reads a value
              outside what it reads today.
            </span>
            <div className="flex flex-column gap-1">
              <label htmlFor="benefitWorklist.dismiss.note">
                Why are you hiding it? (optional)
              </label>
              <InputTextarea
                id="benefitWorklist.dismiss.note"
                value={note}
                rows={3}
                autoResize
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {isError && (
              <Message severity="error" text="It could not be saved." />
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
